import get from 'lodash/get';
import { parseISO, isAfter } from 'date-fns';
import lowerCase from 'lodash/lowerCase';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { generatedTypesNew } from '@css-xp/graphtypes';
import {
  SUBSCRIPTION_CONSTANTS,
  LOOM_PRODUCT_ID,
  ROVO_PRODUCT_ID,
  COLLECTION_PRODUCT_IDS,
} from '@css-xp/ppc-constants';
import { OptedQuantitySource } from '@css-xp/ppc-types';
import { BillEstimateOrderItemDto } from '@css-xp/common-services/src/api/gateways/ccp-gateways/src/ccp-api-order';

const formatDate = (date: Date) => date.toISOString().replace(/\.\d{3}/, '');

const isActiveEntitlement = (entitlement: generatedTypesNew.EntitlementDetails) => {
  return lowerCase(entitlement?.billingStatus ?? '') === lowerCase(generatedTypesNew.BillingStatus.ACTIVE);
};

const isLimitedFreePricingType = (pricingType?: string | null) =>
  Boolean(pricingType === generatedTypesNew.PricingType.LIMITED_FREE);

const isStatusPageTrialV2 = (
  props: generatedTypesNew.EntitlementDetails | generatedTypesNew.LinkedDestinationEntitlement
) => {
  return isStatuspage(props.productId) && props?.edition?.toLowerCase().includes('trial');
};

const isTrialEntitlementV2 = (props: any) => {
  let trialStartDate = props.trialStartDate;
  let trialEndDate = props.trialEndDate;

  if (props?.subscriptionTimestamps) {
    trialStartDate = props?.subscriptionTimestamps?.firstTrialStartTimestamp;
    trialEndDate = props?.subscriptionTimestamps?.lastTrialEndTimestamp;
  }
  const isStatusPage = isStatuspage(props?.productId) || props.baseProduct === EntitlementBaseProduct.STATUS_PAGE;

  return (
    isStatusPageTrialV2(props) ||
    (!isStatusPage && trialStartDate && trialEndDate ? isAfter(parseISO(trialEndDate), new Date()) : false)
  );
};

export const isAnnualTermedSubscription = (item: generatedTypesNew.EntitlementDetails) => {
  return !!(
    item?.billingFrequency === generatedTypesNew.BillingFrequency.ANNUAL &&
    item?.endTimestamp &&
    (!item?.scheduledChanges ||
      item?.scheduledChanges?.subscriptionScheduleAction === generatedTypesNew.SubscriptionScheduleAction.UPDATE) &&
    !(isTrialEntitlementV2(item) && item.endBehaviour === 'CANCEL')
  );
};

export const getNextBillOrEndDate = (
  item: generatedTypesNew.EntitlementDetails,
  isManuallyBilledEnabled?: boolean,
  isEntitlementOnGracePeriod?: boolean
) => {
  if (isEntitlementOnGracePeriod) {
    return item?.latestPeriodEndDate;
  } else if (
    isAnnualTermedSubscription(item) ||
    isLimitedFreePricingType(item?.offering?.pricingType) ||
    (isManuallyBilledEnabled && item?.isExternallyBilled)
  ) {
    return formatDate(new Date(item.endTimestamp as number));
  }
  return item?.latestPeriodEndDate;
};

export const isBilledElsewhereProduct = (productKey: string | null | undefined): boolean => {
  return productKey ? SUBSCRIPTION_CONSTANTS.BILLED_ELSEWHERE_PRODUCT_IDS.includes(productKey) : false;
};

export const isOptedQuantitySourceInherited = (optedQuantitySource: string | null | undefined) =>
  optedQuantitySource === OptedQuantitySource.INHERITED;

export const isLoomProduct = (productId: string | null | undefined): boolean => {
  return productId === LOOM_PRODUCT_ID;
};

export const getProratedCharge = (billEstimate?: BillEstimateOrderItemDto) => {
  const accruedChargeItems = billEstimate?.items?.filter((item) => get(item, 'accruedCharges') === true);
  return accruedChargeItems?.reduce((acc, item) => (acc += item.total), 0) || 0;
};

export const isRovoProduct = (productId: string | null | undefined): boolean => {
  return productId === ROVO_PRODUCT_ID;
};

export const getDestinationLinkedEntitlementsType = (
  isSandboxDecouplingEnabled: boolean,
  isSandboxEntitlement: boolean
) => {
  if (isSandboxDecouplingEnabled) {
    if (isSandboxEntitlement) {
      return [
        generatedTypesNew.LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT,
        generatedTypesNew.LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT,
      ];
    }
    return [
      generatedTypesNew.LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
      generatedTypesNew.LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT,
    ];
  }
  return [
    generatedTypesNew.LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
    generatedTypesNew.LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT,
    generatedTypesNew.LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT,
    generatedTypesNew.LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT,
  ];
};

export const convertBillingFrequencyToAdverb = (billingFrequency: string) => {
  switch (billingFrequency) {
    case generatedTypesNew.BillingFrequency.ANNUAL:
      return 'Annually';
    case generatedTypesNew.BillingFrequency.MONTHLY:
      return 'Monthly';
    default:
      return sentenceCase(billingFrequency);
  }
};

export const isCollectionProductEntitlement = (productId: string | undefined | null) =>
  !!(productId && COLLECTION_PRODUCT_IDS.includes(productId));

export const isCollectionInstanceEntitlement = (
  relatesFromEntitlements: generatedTypesNew.Maybe<generatedTypesNew.RelatedEntitlementDetails>[] | undefined | null
) => {
  return !!relatesFromEntitlements?.some(
    (entitlementRelation) =>
      (entitlementRelation?.relationshipType as unknown) === SUBSCRIPTION_CONSTANTS.COLLECTION_ENTITLEMENT_TYPE
  );
};

export const isEntitlementOnGracePeriod = (entitlement: generatedTypesNew.EntitlementDetails) => {
  return (
    isActiveEntitlement(entitlement) &&
    (!!entitlement?.pauseBilling?.startTimestamp || !!entitlement?.scheduledChanges?.pauseBilling?.startTimestamp)
  );
};
